(() => {
  const DEBUG = true; // Enable debug for troubleshooting
  const log = (...args) => DEBUG && console.log('[QuantityTypeHandler]', ...args);

  const toInt = (v, fallback = 1) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  };

  // Store user selections globally to survive innerHTML replacements
  const userSelections = new Map();

  const getBulkKey = (bulk) => {
    return `${bulk.dataset.index}-${bulk.id || 'unknown'}`;
  };

  const saveUserSelection = (bulk, qtyType, min, increment) => {
    const key = getBulkKey(bulk);
    userSelections.set(key, { qtyType, min, increment, timestamp: Date.now() });
    log('Saved user selection:', key, { qtyType, min, increment });
  };

  const getUserSelection = (bulk) => {
    const key = getBulkKey(bulk);
    return userSelections.get(key);
  };

  const setActive = (btn) => {
    const group = btn.closest('.quantity-type-selector');
    if (!group) return;
    
    group.querySelectorAll('.qty-type-btn').forEach(b => {
      const active = b === btn;
      b.classList.toggle('active', active);
      b.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  };

  const clearActive = (bulk) => {
    const group = bulk.querySelector('.quantity-type-selector');
    if (!group) return;
    
    group.querySelectorAll('.qty-type-btn').forEach(b => {
      b.classList.remove('active');
      b.setAttribute('aria-pressed', 'false');
    });
  };

  const setInputToZero = (bulk) => {
    const input = bulk.querySelector('.quantity__input--display-only');
    if (!input) return;
    
    // Set to current cart quantity
    const cartQty = input.dataset.cartQuantity || '0';
    input.value = cartQty;
  };

  const addToCart = async (btn) => {
    const bulk = btn.closest('quick-add-bulk');
    if (!bulk) return;

    const variantId = btn.dataset.variantId || bulk.dataset.index;
    const qtyToAdd = toInt(btn.dataset.addQty, 1);
    
    log('Adding to cart:', { variantId, qtyToAdd });

    try {
      // Add loading state
      btn.classList.add('loading');
      btn.disabled = true;

      // Get current cart quantity for this variant
      const input = bulk.querySelector('.quantity__input--display-only');
      const currentQty = toInt(input?.value, 0);
      const newQty = currentQty + qtyToAdd;

      log('Current qty:', currentQty, 'New qty:', newQty);

      // Update cart using /cart/update.js
      const body = JSON.stringify({
        updates: {
          [variantId]: newQty
        }
      });

      log('Sending request to /cart/update.js');

      const response = await fetch(`${window.routes?.cart_update_url || '/cart/update.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      log('Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Failed to update cart: ${response.status}`);
      }

      const parsedState = await response.json();
      log('Cart updated successfully. Item count:', parsedState.item_count);
      
      // Update the input value to reflect new cart quantity
      if (input) {
        input.value = String(newQty);
        input.dataset.cartQuantity = String(newQty);
        log('Updated input to:', newQty);
      }

      // CRITICAL: Publish cart update event
      if (typeof window.publish === 'function') {
        log('Publishing cartUpdate event');
        
        // Check if PUB_SUB_EVENTS exists, if not use string directly
        const eventName = window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update';
        
        await window.publish(eventName, { 
          source: 'quick-add', 
          cartData: parsedState,
          variantId: variantId
        });
        
        log('Published cartUpdate event');
      } else {
        log('publish function not available');
      }
      
      // Also dispatch custom DOM event as backup
      const customEvent = new CustomEvent('cart:updated', {
        detail: { 
          cartData: parsedState, 
          variantId: variantId
        },
        bubbles: true
      });
      document.dispatchEvent(customEvent);
      log('Dispatched custom cart:updated event');

      log('Successfully added to cart');

    } catch (error) {
      console.error('[QuantityTypeHandler] Error adding to cart:', error);
      alert('Failed to add item to cart. Please try again.');
    } finally {
      // Remove loading state
      btn.classList.remove('loading');
      btn.disabled = false;
    }
  };

  const removeFromCart = async (variantId) => {
    log('Removing from cart:', { variantId });

    try {
      // Update cart to 0
      const body = JSON.stringify({
        updates: {
          [variantId]: 0
        }
      });

      log('Sending remove request');

      const response = await fetch(`${window.routes?.cart_update_url || '/cart/update.js'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      });

      log('Remove response status:', response.status);

      if (!response.ok) {
        throw new Error('Failed to update cart');
      }

      const parsedState = await response.json();
      log('Cart cleared successfully. Item count:', parsedState.item_count);
      
      // Update all display inputs for this variant
      document.querySelectorAll(`[data-quantity-variant-id="${variantId}"]`).forEach(input => {
        if (input.classList.contains('quantity__input--display-only')) {
          input.value = '0';
          input.dataset.cartQuantity = '0';
        }
      });

      // Publish cart update event
      if (typeof window.publish === 'function') {
        log('Publishing cartUpdate event after removal');
        
        const eventName = window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update';
        
        await window.publish(eventName, { 
          source: 'quick-add', 
          cartData: parsedState,
          variantId: variantId
        });
        
        log('Published cartUpdate event');
      }
      
      // Also dispatch custom event
      const customEvent = new CustomEvent('cart:updated', {
        detail: { 
          cartData: parsedState, 
          variantId: variantId
        },
        bubbles: true
      });
      document.dispatchEvent(customEvent);
      log('Dispatched custom cart:updated event');

      log('Successfully removed from cart');

    } catch (error) {
      console.error('[QuantityTypeHandler] Error removing from cart:', error);
    }
  };

  // Main click handler for quantity type buttons
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('.qty-type-btn');
    if (!btn) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    log('Button clicked:', btn.dataset.qtyType);
    
    // Don't process if already loading
    if (btn.classList.contains('loading') || btn.disabled) {
      log('Button is loading or disabled, ignoring');
      return;
    }
    
    // Set as active and add to cart
    setActive(btn);
    await addToCart(btn);
  });

  // Handle minus button clicks to remove all from cart (high priority - capturing phase)
  document.addEventListener('click', async (e) => {
    const minusBtn = e.target.closest('.quantity__button--remove-all');
    if (!minusBtn) return;

    // Stop all event propagation immediately to prevent other handlers
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    
    const variantId = minusBtn.dataset.variantId;
    if (!variantId) return;
    
    log('Remove all button clicked for variant:', variantId);
    
    // Prevent any default quantity input behavior
    const input = minusBtn.closest('quantity-input')?.querySelector('.quantity__input--display-only');
    if (input) {
      const currentValue = input.value;
      input.dataset.preventChange = 'true';
      
      setTimeout(() => {
        if (input.value !== currentValue) {
          input.value = currentValue;
        }
      }, 10);
    }
    
    await removeFromCart(variantId);
    
    // Clean up the flag
    if (input) {
      delete input.dataset.preventChange;
    }
  }, true); // Use capturing phase to run before other handlers

  // Prevent plus button default behavior since it's disabled
  document.addEventListener('click', (e) => {
    const plusBtn = e.target.closest('.quantity__button--disabled');
    if (!plusBtn) return;

    e.preventDefault();
    e.stopPropagation();
    
    log('Disabled plus button clicked - ignoring');
  });

  // Hook into the cart update process to restore selections after innerHTML replacement
  if (typeof window.subscribe === 'function') {
    const eventName = window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update';
    
    window.subscribe(eventName, (event) => {
      if (event.source === 'quick-add') {
        log('Cart update from quick-add detected');
        
        // Small delay to let the innerHTML replacement finish
        setTimeout(() => {
          document.querySelectorAll('quick-add-bulk').forEach(bulk => {
            initializeBulk(bulk);
          });
        }, 100);
      }
    });
  }

  // Monitor for innerHTML changes
  const observeInnerHTMLChanges = () => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && 
            mutation.target.classList?.contains('quick-add-bulk')) {
          
          log('quick-add-bulk innerHTML was replaced');
          
          const bulk = mutation.target;
          setTimeout(() => {
            initializeBulk(bulk);
          }, 50);
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return observer;
  };

  const initializeBulk = (bulk) => {
    log('Initializing bulk with no active state');
    
    clearActive(bulk);
    setInputToZero(bulk);
  };

  // Initialize function
  const init = (root = document) => {
    log('Initializing quantity selectors');
    
    root.querySelectorAll('quick-add-bulk').forEach(bulk => {
      const key = getBulkKey(bulk);
      log('Processing bulk:', key);
      
      initializeBulk(bulk);
    });
  };

  // Start everything
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      log('DOM Content Loaded, initializing...');
      log('PubSub available:', typeof window.publish, typeof window.subscribe);
      log('PUB_SUB_EVENTS:', window.PUB_SUB_EVENTS);
      init();
      observeInnerHTMLChanges();
    }, { once: true });
  } else {
    log('DOM already loaded, initializing...');
    log('PubSub available:', typeof window.publish, typeof window.subscribe);
    log('PUB_SUB_EVENTS:', window.PUB_SUB_EVENTS);
    init();
    observeInnerHTMLChanges();
  }

  // Handle section loads
  document.addEventListener('shopify:section:load', (evt) => {
    log('Section loaded, initializing new elements');
    setTimeout(() => init(evt.target), 100);
  });

  // Emergency fallback: ensure display inputs show correct cart quantities
  setInterval(() => {
    document.querySelectorAll('.quantity__input--display-only').forEach(input => {
      const cartQty = input.dataset.cartQuantity || '0';
      if (input.value !== cartQty) {
        log('Emergency sync - updating display input');
        input.value = cartQty;
      }
    });
  }, 2000);

  // Expose for debugging
  window.QuantityTypeHandler = {
    init,
    initializeBulk,
    selections: userSelections,
    addToCart,
    removeFromCart,
    debug: {
      logSelections: () => log('Current selections:', Object.fromEntries(userSelections)),
      clearSelections: () => userSelections.clear(),
      testPublish: async () => {
        log('Testing publish...');
        const eventName = window.PUB_SUB_EVENTS?.cartUpdate || 'cart-update';
        await window.publish(eventName, { 
          source: 'test', 
          cartData: { item_count: 999 }
        });
        log('Publish test complete');
      }
    }
  };

  log('QuantityTypeHandler initialized');
})();